import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import AdminNewSidebar from "./AdminNewSidebar";
import Header from "components/Header";
import DeleteModal from "components/modals/DeleteModal";
import apis from "services";
import useNavigate from "hooks/Navigate";
import { formatDate } from "utils/formatDate";
import { removeHTMLTags } from "utils/removeHTMLTags";
import utils from "utils";

// Tab buttons for users detils 
const TAB_BUTTONS = [
  { key: "user_details", label: "User Details" },
  { key: "transactions", label: "Transactions" },
  { key: "profit_details", label: "Profit Details" },
  { key: "withdraw_details", label: "Withdraw" },
  { key: "wallet_details", label: "Wallet Details" },
];

const moneyFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
});

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return moneyFormatter.format(value);
  return String(value);
};

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return formatValue(value);
  return moneyFormatter.format(numeric);
};

const resolveContractUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();
  if (!cleanUrl) return "";

  if (/^https?:\/\//i.test(cleanUrl)) {
    return cleanUrl;
  }

  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  if (!baseUrl) return cleanUrl;

  return cleanUrl.startsWith("/")
    ? `${baseUrl}${cleanUrl}`
    : `${baseUrl}/${cleanUrl}`;
};

const getStatusColor = (status) => {
  if (!status) return "default";
  if (status === "approved") return "success";
  if (status === "cancelled") return "warning";
  if (status === "rejected") return "error";
  if (status === "processing") return "info";
  return "default";
};

const SectionCard = ({ title, subtitle, children, actions }) => (
  <Card
    sx={{
      borderRadius: 4,
      border: "1px solid rgba(230, 14, 16, 0.10)",
      boxShadow: "0 18px 50px rgba(15, 23, 42, 0.06)",
      overflow: "hidden",
    }}
  >
    <CardContent sx={{ p: { xs: 2, md: 3.5 } }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "#E60E10",
              fontWeight: 700,
              letterSpacing: 1.4,
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {actions}
      </Stack>
      {children}
    </CardContent>
  </Card>
);

const DetailTile = ({ label, value, helper, accent = false }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: 3,
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: accent ? "rgba(230, 14, 16, 0.03)" : "#fff",
      height: "100%",
    }}
  >
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        textTransform: "uppercase",
        letterSpacing: 1,
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{
        mt: 0.75,
        fontWeight: 600,
        wordBreak: "break-word",
        lineHeight: 1.4,
      }}
    >
      {formatValue(value)}
    </Typography>
    {helper ? (
      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
        {helper}
      </Typography>
    ) : null}
  </Box>
);

const TabButton = ({ active, onClick, children }) => (
  <Button
    onClick={onClick}
    variant={active ? "contained" : "outlined"}
    sx={{
      borderRadius: "999px",
      textTransform: "none",
      px: 2.25,
      ...(active
        ? {
            background: "linear-gradient(135deg, #E60E10 0%, #ff6b4a 100%)",
            boxShadow: "none",
            "&:hover": {
              background: "linear-gradient(135deg, #cc0d0f 0%, #ff5b35 100%)",
              boxShadow: "none",
            },
          }
        : {
            borderColor: "rgba(15, 23, 42, 0.14)",
            color: "#111827",
            "&:hover": {
              borderColor: "#E60E10",
              backgroundColor: "rgba(230, 14, 16, 0.04)",
            },
          }),
    }}
  >
    {children}
  </Button>
);

const TableSection = ({
  title,
  subtitle,
  rows,
  columns,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  serverPagination = false,
  emptyMessage = "No records found",
  gridHeight = 560,
}) => (
  <SectionCard title={title} subtitle={subtitle}>
    <Box
      sx={{
        width: "100%",
        height: gridHeight,
        p: { xs: 1.5, md: 2 },
        boxSizing: "border-box",
        "& .MuiDataGrid-root": {
          border: 0,
        },
        "& .MuiDataGrid-columnHeaders": {
          backgroundColor: "rgba(15, 23, 42, 0.03)",
          borderRadius: 2,
        },
        "& .MuiDataGrid-cell": {
          borderBottom: "1px solid rgba(15, 23, 42, 0.06)",
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        pagination
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        rowsPerPageOptions={[5, 10, 25]}
        localeText={{ noRowsLabel: emptyMessage }}
        sx={{
          border: 0,
          height: "100%",
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
        }}
        {...(serverPagination
          ? {
              paginationMode: "server",
              rowCount,
              page: paginationModel?.page ?? 0,
              pageSize: paginationModel?.pageSize ?? 10,
              onPageChange: (page) =>
                onPaginationModelChange?.({
                  page,
                  pageSize: paginationModel?.pageSize ?? 10,
                }),
              onPageSizeChange: (pageSize) =>
                onPaginationModelChange?.({
                  page: 0,
                  pageSize,
                }),
            }
          : {})}
      />
    </Box>
  </SectionCard>
);

const UserDetails = () => {
  const navigate = useNavigate();
  const { id: userId } = useParams();

  const [tab, setTab] = useState("user_details");
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [userDetails, setUserDetails] = useState(null);
  const [walletDetails, setWalletDetails] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [transactionsTotal, setTransactionsTotal] = useState(0);
  const [profitDetails, setProfitDetails] = useState([]);
  const [profitTotal, setProfitTotal] = useState(0);
  const [withdrawDetails, setWithdrawDetails] = useState([]);
  const [contractDetails, setContractDetails] = useState(null);
  const [userMessage, setUserMessage] = useState("");
  const [walletMessage, setWalletMessage] = useState("");
  const [transactionsMessage, setTransactionsMessage] = useState("");
  const [profitMessage, setProfitMessage] = useState("");
  const [withdrawMessage, setWithdrawMessage] = useState("");
  const [contractMessage, setContractMessage] = useState("");
  const [walletLoaded, setWalletLoaded] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [profitLoading, setProfitLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [transactionPagination, setTransactionPagination] = useState({
    page: 0,
    pageSize: 10,
  });
  const [profitPagination, setProfitPagination] = useState({
    page: 0,
    pageSize: 10,
  });

  const loadCoreData = useCallback(async ({ silent = false } = {}) => {
    if (!userId) return;

    if (!silent) setLoading(true);
    setUserMessage("");
    setContractDetails(null);
    setContractMessage("");
    setWalletDetails(null);
    setWalletMessage("");
    setWalletLoaded(false);

    try {
      const [userResult] = await Promise.allSettled([
        apis.getUserAllDetails(userId, "user_details"),
      ]);

      if (userResult.status === "fulfilled") {
        const payload = userResult.value?.data;
        setUserDetails(payload?.status ? payload?.user || null : null);
        if (!payload?.status) {
          setUserMessage(payload?.message || "No user record found");
        }
      } else {
        setUserDetails(null);
        setUserMessage(userResult.reason?.message || "Unable to load user details");
      }

    } catch (error) {
      toast.error(error?.message || "Unable to load user details");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userId]);

  const loadWallet = useCallback(async () => {
    if (!userId) return;

    setWalletLoading(true);
    setWalletMessage("");

    try {
      const response = await apis.getWallet(userId);
      const payload = response?.data;

      setWalletDetails(payload?.wallet || null);
      if (!payload?.status) {
        setWalletMessage(payload?.message || "Wallet not created yet");
      }
    } catch (error) {
      setWalletDetails(null);
      setWalletMessage(error?.message || "Unable to load wallet details");
    } finally {
      setWalletLoading(false);
      setWalletLoaded(true);
    }
  }, [userId]);

  const loadContract = useCallback(async () => {
    if (!userId) return;

    setContractLoading(true);
    setContractMessage("");

    try {
      const response = await apis.getUserAllDetails(userId, "kyc_details");
      const payload = response?.data;

      if (payload?.status && payload?.user) {
        setContractDetails(payload.user);
      } else {
        setContractDetails(null);
        setContractMessage(payload?.message || "No contract file found");
      }
    } catch (error) {
      setContractDetails(null);
      setContractMessage(error?.message || "Unable to load contract file");
    } finally {
      setContractLoading(false);
    }
  }, [userId]);

  const loadTransactions = useCallback(async (model) => {
    if (!userId) return;

    setTransactionsLoading(true);
    setTransactionsMessage("");
    try {
      const response = await apis.getAdminUserTransactions(userId, {
        page: model.page + 1,
        limit: model.pageSize,
      });
      const payload = response?.data;

      if (payload?.status) {
        setTransactions(payload?.transactions || []);
        setTransactionsTotal(payload?.total || 0);
      } else {
        setTransactions([]);
        setTransactionsTotal(0);
        setTransactionsMessage(payload?.message || "No transactions found");
      }
    } catch (error) {
      setTransactions([]);
      setTransactionsTotal(0);
      setTransactionsMessage(error?.message || "Unable to load transactions");
    } finally {
      setTransactionsLoading(false);
    }
  }, [userId]);

  const loadProfit = useCallback(async (model) => {
    if (!userId) return;

    setProfitLoading(true);
    setProfitMessage("");
    try {
      const response = await apis.getAdminUserProfit(userId, {
        page: model.page + 1,
        limit: model.pageSize,
      });
      const payload = response?.data;

      if (payload?.status) {
        setProfitDetails(payload?.profits || []);
        setProfitTotal(payload?.total || 0);
      } else {
        setProfitDetails([]);
        setProfitTotal(0);
        setProfitMessage(payload?.message || "Profit record not found");
      }
    } catch (error) {
      setProfitDetails([]);
      setProfitTotal(0);
      setProfitMessage(error?.message || "Unable to load profit details");
    } finally {
      setProfitLoading(false);
    }
  }, [userId]);

  const loadWithdrawals = useCallback(async () => {
    if (!userId) return;

    setWithdrawLoading(true);
    setWithdrawMessage("");
    try {
      const response = await apis.getUserWithdrawl(userId);
      const payload = response?.data;

      if (payload?.status) {
        setWithdrawDetails(payload?.withDrawl || []);
      } else {
        setWithdrawDetails([]);
        setWithdrawMessage(payload?.message || "Withdraw records not found");
      }
    } catch (error) {
      setWithdrawDetails([]);
      setWithdrawMessage(error?.message || "Unable to load withdrawals");
    } finally {
      setWithdrawLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    setTab("user_details");
    loadCoreData();
  }, [loadCoreData]);

  useEffect(() => {
    if (tab === "wallet_details" && !walletLoaded && !walletLoading) {
      loadWallet();
    }
  }, [loadWallet, tab, walletLoaded, walletLoading]);

  useEffect(() => {
    if (tab === "withdraw_details") {
      loadWithdrawals();
    }
  }, [loadWithdrawals, tab]);

  useEffect(() => {
    if (tab === "transactions") {
      loadTransactions(transactionPagination);
    }
  }, [loadTransactions, tab, transactionPagination]);

  useEffect(() => {
    if (tab === "profit_details") {
      loadProfit(profitPagination);
    }
  }, [loadProfit, tab, profitPagination]);

  useEffect(() => {
    if (tab === "transactions" && !contractDetails && !contractLoading && !contractMessage) {
      loadContract();
    }
  }, [contractDetails, contractLoading, contractMessage, loadContract, tab]);

  const displayName = useMemo(
    () =>
      userDetails?.name ||
      userDetails?.fullName ||
      userDetails?.nickName ||
      "User",
    [userDetails]
  );

  const avatarSrc = userDetails?.image ? utils.serverImg(userDetails.image) : "";
  const createdAt = userDetails?.createdAt ? formatDate(userDetails.createdAt) : "—";
  const walletCreatedAt = walletDetails?.createdAt
    ? formatDate(walletDetails.createdAt)
    : "—";

  const userTiles = useMemo(
    () => [
      { label: "Full name", value: userDetails?.name || displayName },
      { label: "Nickname", value: userDetails?.nickName },
      { label: "Email", value: userDetails?.email },
      { label: "Phone", value: userDetails?.phone },
      { label: "Wallet ID", value: userDetails?.wallet, accent: true },
      { label: "Account type", value: userDetails?.type },
      { label: "Category", value: userDetails?.category },
      { label: "Verified", value: userDetails?.isVerified },
      { label: "Investor", value: userDetails?.isInvestor },
      { label: "Referral ID", value: userDetails?.referralId },
      { label: "Created at", value: createdAt },
    ],
    [createdAt, displayName, userDetails]
  );

  const walletTiles = useMemo(
    () => [
      { label: "Amount", value: walletDetails?.amount },
      { label: "Shares", value: walletDetails?.shares },
      { label: "Balance", value: walletDetails?.balance },
      { label: "Dividend", value: walletDetails?.dividend },
      { label: "JTC token", value: walletDetails?.jtcToken },
      { label: "Wallet type", value: walletDetails?.type },
      { label: "KYC status", value: walletDetails?.is_kyc },
      { label: "Profile completed", value: walletDetails?.isProfileCompleted },
      { label: "Beneficiary", value: walletDetails?.isBeneficiary },
      { label: "Wallet created", value: walletCreatedAt },
    ],
    [walletCreatedAt, walletDetails]
  );

  const transactionRows = useMemo(
    () =>
      transactions.map((item, index) => ({
        id: index + 1,
        storeName: removeHTMLTags(item?.storeName || item?.storeId?.title_en || ""),
        transactionId: item?.transactionId || "—",
        type: item?.type || "—",
        amountInvested: item?.amountInvested || 0,
        amountReceived: item?.amountReceived ?? item?.amountInvested ?? 0,
        dividendRemainder: item?.dividendRemainder || 0,
        sharePrice: item?.sharePrice || 0,
        purchasedShare: item?.purchasedShare || 0,
        lots: (item?.lotAllocations || []).map((allocation) => allocation.lotCode).filter(Boolean).join(", ") || "N/A",
        folios: (item?.lotAllocations || []).map((allocation) =>
          allocation.firstFolio && allocation.lastFolio
            ? `${allocation.firstFolio} - ${allocation.lastFolio}`
            : "Assigned after confirmation"
        ).join(", ") || "N/A",
        purchasedAt: item?.purchasedAt ? formatDate(item.purchasedAt) : "—",
      })),
    [transactions]
  );

  const profitRows = useMemo(
    () =>
      profitDetails.map((item, index) => ({
        id: index + 1,
        storeName: removeHTMLTags(item?.storeName || ""),
        transactionType: item?.transactionType || "—",
        transactionAmount: item?.transactionAmount || 0,
        transactionDate: item?.transactionDate ? formatDate(item.transactionDate) : "—",
        profitAmount: item?.profitAmount || 0,
        totalProfitAmount: item?.totalProfitAmount || 0,
        loss: item?.loss || 0,
      })),
    [profitDetails]
  );

  const withdrawRows = useMemo(
    () =>
      withdrawDetails.map((item, index) => ({
        id: index + 1,
        transactionId: item?.transactionId || "—",
        amount: item?.amount || 0,
        status: item?.status || "pending",
        type: item?.type || "—",
        bankName: item?.bankName || "—",
        accountNo: item?.accountNo || item?.accountNumber || "—",
        rfcNo: item?.rfcNo || "—",
        document: item?.document || item?.file || "",
        createdAt: item?.createdAt ? formatDate(item.createdAt) : "—",
      })),
    [withdrawDetails]
  );

  const transactionColumns = useMemo(
    () => [
      { field: "id", headerName: "#", width: 70 },
      {
        field: "storeName",
        headerName: "Store",
        minWidth: 220,
        flex: 1,
      },
      {
        field: "transactionId",
        headerName: "Transaction ID",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "type",
        headerName: "Type",
        width: 130,
      },
      {
        field: "amountInvested",
        headerName: "Invested",
        width: 130,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "amountReceived",
        headerName: "Received",
        width: 130,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "dividendRemainder",
        headerName: "To Dividends",
        width: 140,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "sharePrice",
        headerName: "Share Price",
        width: 130,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "purchasedShare",
        headerName: "Shares",
        width: 110,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "lots",
        headerName: "Lots",
        minWidth: 150,
      },
      {
        field: "folios",
        headerName: "Permanent folios",
        minWidth: 300,
        flex: 1,
      },
      {
        field: "purchasedAt",
        headerName: "Purchased At",
        minWidth: 170,
        flex: 1,
      },
    ],
    []
  );

  const profitColumns = useMemo(
    () => [
      { field: "id", headerName: "#", width: 70 },
      {
        field: "storeName",
        headerName: "Store",
        minWidth: 200,
        flex: 1,
      },
      {
        field: "transactionType",
        headerName: "Type",
        width: 120,
      },
      {
        field: "transactionAmount",
        headerName: "Transaction Amount",
        minWidth: 170,
        flex: 1,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "transactionDate",
        headerName: "Transaction Date",
        minWidth: 170,
        flex: 1,
      },
      {
        field: "profitAmount",
        headerName: "Profit Amount",
        minWidth: 150,
        flex: 1,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "totalProfitAmount",
        headerName: "Total Profit Amount",
        minWidth: 180,
        flex: 1,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "loss",
        headerName: "Loss",
        minWidth: 130,
        flex: 1,
        valueFormatter: ({ value }) => formatMoney(value),
      },
    ],
    []
  );

  const withdrawColumns = useMemo(
    () => [
      { field: "id", headerName: "#", width: 70 },
      {
        field: "transactionId",
        headerName: "Transaction ID",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "amount",
        headerName: "Amount",
        width: 120,
        valueFormatter: ({ value }) => formatMoney(value),
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: ({ value }) => (
          <Chip
            label={value}
            color={getStatusColor(value)}
            size="small"
            sx={{ textTransform: "capitalize", fontWeight: 600 }}
          />
        ),
      },
      {
        field: "type",
        headerName: "Type",
        width: 130,
      },
      {
        field: "bankName",
        headerName: "Bank",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "accountNo",
        headerName: "Account No",
        minWidth: 180,
        flex: 1,
      },
      {
        field: "rfcNo",
        headerName: "RFC",
        minWidth: 140,
        flex: 1,
      },
      {
        field: "document",
        headerName: "Document",
        width: 130,
        renderCell: ({ value }) =>
          value ? (
            <Button
              size="small"
              variant="text"
              component="a"
              href={value}
              target="_blank"
              rel="noreferrer"
              sx={{ textTransform: "none" }}
            >
              View
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          ),
      },
      {
        field: "createdAt",
        headerName: "Created At",
        minWidth: 180,
        flex: 1,
      },
    ],
    []
  );

  const transactionSummary = useMemo(
    () => [
      { label: "Transactions", value: transactionsTotal || transactionRows.length },
      {
        label: "Invested",
        value: formatMoney(
          transactionRows.reduce((sum, item) => sum + Number(item.amountInvested || 0), 0)
        ),
      },
      {
        label: "Shares",
        value: formatMoney(
          transactionRows.reduce((sum, item) => sum + Number(item.purchasedShare || 0), 0)
        ),
      },
    ],
    [transactionRows, transactionsTotal]
  );

  const profitSummary = useMemo(
    () => [
      { label: "Entries", value: profitTotal || profitRows.length },
      {
        label: "Profit Total",
        value: formatMoney(
          profitRows.reduce((sum, item) => sum + Number(item.profitAmount || 0), 0)
        ),
      },
      {
        label: "Loss Total",
        value: formatMoney(profitRows.reduce((sum, item) => sum + Number(item.loss || 0), 0)),
      },
    ],
    [profitRows, profitTotal]
  );

  const withdrawSummary = useMemo(() => {
    const approved = withdrawRows.filter((item) => item.status === "approved").length;
    const pending = withdrawRows.filter(
      (item) => !["approved", "cancelled", "rejected"].includes(item.status)
    ).length;

    return [
      { label: "Requests", value: withdrawRows.length },
      { label: "Approved", value: approved },
      { label: "Pending", value: pending },
    ];
  }, [withdrawRows]);

  const handleTabChange = async (nextTab) => {
    setTab(nextTab);
    if (nextTab === "user_details") {
      await loadCoreData({ silent: true });
    } else if (nextTab === "wallet_details" && !walletLoaded) {
      await loadWallet();
    }
  };

  const handleTransactionPaginationModelChange = (model) => {
    setTransactionPagination(model);
  };

  const handleProfitPaginationModelChange = (model) => {
    setProfitPagination(model);
  };

  const handleViewContract = () => {
    const contractUrl = resolveContractUrl(
      contractDetails?.file || contractDetails?.file_before_signature
    );

    if (!contractUrl) {
      toast.info(contractMessage || "No contract file available");
      return;
    }

    window.open(contractUrl, "_blank", "noopener,noreferrer");
  };

  const handleVerify = async () => {
    const shouldDisable = Boolean(userDetails?.isVerified);
    setBusyAction(shouldDisable ? "disable" : "verify");
    try {
      const res = shouldDisable
        ? await apis.disableUser(userId)
        : await apis.approveUser(userId);
      if (res?.data?.status) {
        toast.success(res?.data?.message || "User status updated");
        await loadCoreData({ silent: true });
      } else {
        toast.error(res?.data?.message || "Unable to update user status");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to update user status");
    } finally {
      setBusyAction("");
    }
  };

  const handleResetEmail = async () => {
    if (!userDetails?.email) {
      toast.error("User email is missing");
      return;
    }

    setBusyAction("reset");
    try {
      const res = await apis.resetEmail({
        email: userDetails.email,
        fullName: userDetails.fullName || userDetails.name || displayName,
      });

      if (res?.data?.status) {
        toast.success(res?.data?.message || "Reset email sent");
      } else {
        toast.error(res?.data?.message || "Unable to send reset email");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to send reset email");
    } finally {
      setBusyAction("");
    }
  };

  const handleDelete = async () => {
    setBusyAction("delete");
    try {
      const res = await apis.deleteUser(userId);
      if (res?.data?.status) {
        toast.success(res?.data?.message || "User deleted successfully");
        navigate("/admin/users");
      } else {
        toast.error(res?.data?.message || "Unable to delete user");
      }
    } catch (error) {
      toast.error(error?.message || "Unable to delete user");
    } finally {
      setBusyAction("");
      setDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Box className="dashboard-main">
        <AdminNewSidebar />
        <div className="dashboard-section-main">
          <Box
            component="main"
            sx={{ flexGrow: 1, height: "100vh", overflow: "auto" }}
            className="custom-scroll"
          >
            <Header />
            <div className="loader-container">
              <div className="loader" />
            </div>
          </Box>
        </div>
      </Box>
    );
  }

  return (
    <>
      <Box className="dashboard-main">
        <AdminNewSidebar />
        <div className="dashboard-section-main">
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              height: "100vh",
              overflow: "auto",
            }}
            className="custom-scroll"
          >
            <Header />

            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }} className="userContainer">
              <Stack spacing={3}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "flex-start", md: "center" }}
                  justifyContent="space-between"
                  gap={2}
                >
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800 }}>
                      User Details
                    </Typography>
                  </Box>

                  <Button
                    onClick={() => navigate("/admin/users")}
                    variant="outlined"
                    sx={{
                      borderRadius: "999px",
                      textTransform: "none",
                      borderColor: "rgba(230, 14, 16, 0.25)",
                      color: "#E60E10",
                      px: 2.5,
                      "&:hover": {
                        borderColor: "#E60E10",
                        backgroundColor: "rgba(230, 14, 16, 0.04)",
                      },
                    }}
                  >
                    Back to Users
                  </Button>
                </Stack>

                <Stack
                  direction="row"
                  spacing={1}
                  useFlexGap
                  flexWrap="wrap"
                  sx={{ justifyContent: { xs: "flex-start", md: "center" } }}
                >
                  {TAB_BUTTONS.map((item) => (
                    <TabButton
                      key={item.key}
                      active={tab === item.key}
                      onClick={() => handleTabChange(item.key)}
                    >
                      {item.label}
                    </TabButton>
                  ))}
                </Stack>

                {userMessage ? <Alert severity="warning">{userMessage}</Alert> : null}
                {walletMessage ? <Alert severity="info">{walletMessage}</Alert> : null}

                {tab === "user_details" ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <SectionCard title="User Profile">
                        <Stack spacing={3}>
                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={2}
                            alignItems={{ xs: "flex-start", sm: "center" }}
                          >
                            <Avatar
                              src={avatarSrc}
                              sx={{
                                width: 84,
                                height: 84,
                                bgcolor: "rgba(230, 14, 16, 0.12)",
                                color: "#E60E10",
                                fontWeight: 800,
                                fontSize: 28,
                              }}
                            >
                              {displayName?.[0]?.toUpperCase() || "U"}
                            </Avatar>

                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                {displayName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "text.secondary", mt: 0.5 }}
                              >
                                {userDetails?.email || "No email available"}
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={1}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{ mt: 1.5 }}
                              >
                                <Chip
                                  label={userDetails?.isVerified ? "Verified" : "Not verified"}
                                  color={userDetails?.isVerified ? "success" : "error"}
                                  size="small"
                                />
                                <Chip
                                  label={userDetails?.isInvestor ? "Investor" : "User"}
                                  variant="outlined"
                                  size="small"
                                />
                                {userDetails?.type ? (
                                  <Chip label={userDetails.type} variant="outlined" size="small" />
                                ) : null}
                              </Stack>
                            </Box>
                          </Stack>

                          <Grid container spacing={2}>
                            {userTiles.map((tile) => (
                              <Grid item xs={12} sm={6} key={tile.label}>
                                <DetailTile
                                  label={tile.label}
                                  value={tile.value}
                                  accent={tile.accent}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Stack>
                      </SectionCard>
                    </Grid>

                    <Grid item xs={12}>
                      <SectionCard
                        title="Action Items"

                      >
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.5}
                          useFlexGap
                          flexWrap="wrap"
                        >
                          <Button
                            variant="contained"
                            onClick={() => navigate(`/admin/users/add/${userId}`)}
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              background:
                                "linear-gradient(135deg, #E60E10 0%, #ff6b4a 100%)",
                              boxShadow: "none",
                            }}
                          >
                            Edit User
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleVerify}
                            disabled={busyAction === "verify" || busyAction === "disable"}
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              borderColor: "rgba(15, 23, 42, 0.14)",
                              color: "#111827",
                              boxShadow: "none",
                            }}
                          >
                            {busyAction === "verify" || busyAction === "disable"
                              ? "Updating..."
                              : userDetails?.isVerified
                                ? "Disable User"
                                : "Verify User"}
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={() => navigate(`/admin/admin-invest/${userId}`)}
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              borderColor: "rgba(15, 23, 42, 0.14)",
                              color: "#111827",
                              boxShadow: "none",
                            }}
                          >
                            Manual Invest
                          </Button>
                          <Button
                            variant="outlined"
                            onClick={handleResetEmail}
                            disabled={busyAction === "reset"}
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              borderColor: "rgba(15, 23, 42, 0.14)",
                              color: "#111827",
                              boxShadow: "none",
                            }}
                          >
                            {busyAction === "reset"
                              ? "Sending..."
                              : "Reset Password Email"}
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            onClick={() => setDeleteModal(true)}
                            disabled={Boolean(userDetails?.isDeleted) || busyAction === "delete"}
                            sx={{
                              borderRadius: "999px",
                              textTransform: "none",
                              boxShadow: "none",
                            }}
                          >
                            {userDetails?.isDeleted
                              ? "Already Deleted"
                              : busyAction === "delete"
                                ? "Deleting..."
                                : "Delete User"}
                          </Button>
                        </Stack>
                      </SectionCard>
                    </Grid>
                  </Grid>
                ) : null}

                {tab === "wallet_details" ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12} lg={7}>
                      <SectionCard
                        title="Wallet Overview"
                    
                      >
                          {walletLoading ? (
                          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                            <div className="loader" />
                          </Box>
                        ) : (
                          <Stack spacing={3}>
                            <Box
                              sx={{
                                p: 2.5,
                                borderRadius: 3,
                                background:
                                  "linear-gradient(135deg, rgba(230, 14, 16, 0.05), rgba(255,255,255,1))",
                                border: "1px solid rgba(230, 14, 16, 0.10)",
                              }}
                            >
                              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                {walletDetails?.type || "Wallet"}
                              </Typography>
                              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                                Wallet ID: {userDetails?.wallet || "—"}
                              </Typography>
                            </Box>

                            <Grid container spacing={2}>
                              {walletTiles
                                .filter((item) =>
                                  [
                                    "Amount",
                                    "Shares",
                                    "Balance",
                                    "Dividend",
                                    "JTC token",
                                  ].includes(item.label)
                                )
                                .map((tile) => (
                                  <Grid item xs={12} sm={6} key={tile.label}>
                                    <DetailTile label={tile.label} value={tile.value} />
                                  </Grid>
                                ))}
                            </Grid>
                          </Stack>
                        )}
                      </SectionCard>
                    </Grid>

                    <Grid item xs={12} lg={5}>
                      <SectionCard title="Wallet Status">
                        {walletLoading ? (
                          <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                            <div className="loader" />
                          </Box>
                        ) : (
                          <Stack spacing={2.25}>
                            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                              <Chip
                                label={`KYC: ${formatValue(walletDetails?.is_kyc)}`}
                                color={walletDetails?.is_kyc ? "success" : "default"}
                              />
                              <Chip
                                label={`Profile: ${formatValue(walletDetails?.isProfileCompleted)}`}
                                color={walletDetails?.isProfileCompleted ? "success" : "default"}
                              />
                              <Chip
                                label={`Beneficiary: ${formatValue(walletDetails?.isBeneficiary)}`}
                                variant="outlined"
                              />
                            </Stack>

                            <Grid container spacing={2}>
                            {walletTiles
                              .filter((item) =>
                                [
                                  "Wallet type",
                                  "Wallet created",
                                    "KYC status",
                                    "Profile completed",
                                    "Beneficiary",
                                  ].includes(item.label)
                                )
                                .map((tile) => (
                                  <Grid item xs={12} key={tile.label}>
                                    <DetailTile label={tile.label} value={tile.value} />
                                  </Grid>
                                ))}
                            </Grid>
                          </Stack>
                        )}
                      </SectionCard>
                    </Grid>
                  </Grid>
                ) : null}

                {tab === "transactions" ? (
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      {transactionSummary.map((item) => (
                        <Grid item xs={12} md={4} key={item.label}>
                          <DetailTile label={item.label} value={item.value} accent />
                        </Grid>
                      ))}
                    </Grid>

                    {transactionsMessage ? <Alert severity="info">{transactionsMessage}</Alert> : null}

                    <TableSection
                      title="All Transactions"
                      rows={transactionRows}
                      columns={transactionColumns}
                      loading={transactionsLoading}
                      rowCount={transactionsTotal}
                      paginationModel={transactionPagination}
                      onPaginationModelChange={handleTransactionPaginationModelChange}
                      serverPagination
                      gridHeight={620}
                      emptyMessage="No transactions available for this user"
                    />

                    <SectionCard
                      title="Contract File"

                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700 }}>
                            {contractDetails?.isContractSign
                              ? "Signed contract"
                              : "Contract draft"}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                            {contractDetails?.file || contractDetails?.file_before_signature
                              ? "View the user contract PDF below."
                              : "No contract file is available for this user yet."}
                          </Typography>
                        </Box>

                        <Button
                          variant="contained"
                          onClick={handleViewContract}
                          disabled={contractLoading || !(contractDetails?.file || contractDetails?.file_before_signature)}
                          sx={{
                            borderRadius: "999px",
                            textTransform: "none",
                            background:
                              "linear-gradient(135deg, #E60E10 0%, #ff6b4a 100%)",
                            boxShadow: "none",
                          }}
                        >
                          {contractLoading ? "Loading..." : "View file"}
                        </Button>
                      </Stack>

                      {contractMessage ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          {contractMessage}
                        </Alert>
                      ) : null}
                    </SectionCard>
                  </Stack>
                ) : null}

                {tab === "profit_details" ? (
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      {profitSummary.map((item) => (
                        <Grid item xs={12} md={4} key={item.label}>
                          <DetailTile label={item.label} value={item.value} accent />
                        </Grid>
                      ))}
                    </Grid>

                    {profitMessage ? <Alert severity="info">{profitMessage}</Alert> : null}

                    <TableSection
                      title="Profit Details"
                      rows={profitRows}
                      columns={profitColumns}
                      loading={profitLoading}
                      rowCount={profitTotal}
                      paginationModel={profitPagination}
                      onPaginationModelChange={handleProfitPaginationModelChange}
                      serverPagination
                      gridHeight={620}
                      emptyMessage="Profit details will appear once the user has earning records"
                    />
                  </Stack>
                ) : null}

                {tab === "withdraw_details" ? (
                  <Stack spacing={2}>
                    <Grid container spacing={2}>
                      {withdrawSummary.map((item) => (
                        <Grid item xs={12} md={4} key={item.label}>
                          <DetailTile label={item.label} value={item.value} accent />
                        </Grid>
                      ))}
                    </Grid>

                    {withdrawMessage ? <Alert severity="info">{withdrawMessage}</Alert> : null}

                    <TableSection
                      title="Withdrawals"
                      rows={withdrawRows}
                      columns={withdrawColumns}
                      loading={withdrawLoading}
                      gridHeight={620}
                      emptyMessage="No withdrawal requests found for this user"
                    />
                  </Stack>
                ) : null}
              </Stack>
            </Container>
          </Box>
        </div>
      </Box>

      <DeleteModal
        show={deleteModal}
        handleClose={() => setDeleteModal(false)}
        handleSubmit={handleDelete}
        loader={busyAction === "delete"}
        id={userId}
        title="Delete User"
      />
    </>
  );
};

export default UserDetails;
